import { Divider, Flex, IconFont, Text, Tooltip } from '@byecode/ui'
import { APPLICATION_VERSION_STATUS } from '@lighthouse/core'
import { useAtomData } from '@lighthouse/shared'
import mitt from 'mitt'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { appAtom, applicationVersionAtom } from '@/atoms/application/state'
import { PublishWxPrograms } from '@/components/ApplicationPublish/PublishWxPrograms'
import { SharedOtherPlatforms } from '@/components/ApplicationPublish/ShareOtherPlatforms'
import { SharePoster } from '@/components/ApplicationPublish/SharePoster'
import { SharePrivateLink } from '@/components/ApplicationPublish/SharePrivateLink'
import { ShareQrCode } from '@/components/ApplicationPublish/ShareQrCode'
import { otherPlatformEmitter } from '@/containers/OtherPlatforms/OtherPlatformEvent'
import { WxProgramsAuditStatus } from '@/services/types'
import { useWxMiniProgramsAuthorizationResult } from '@/shared/reusable'

const List = styled.ul`
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 16px -12px;
`
const ListItem = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 12px;
    cursor: pointer;
    &:hover {
        background-color: var(--color-gray-100);
    }
`
const ListItemIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    color: var(--color-white);
`

const SCxGreenRound = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #12b76a;
`

export type PublishedPanelType = 'DEFAULT' | 'POSTER' | 'QRCODE' | 'SHARE' | 'PRIVATE' | 'WX_PROGRAMS'

interface Props {
    defaultPanel?: PublishedPanelType
    isPreview?: boolean
    header?: React.ReactNode
    footer?: React.ReactNode
    showShareContent?: boolean
    onClose?: () => void
}

type Event = {
    openPublishedPanel: PublishedPanelType
}

export const publishEmitter = mitt<Event>()

export const openPublishedPanel = (params: PublishedPanelType) => {
    publishEmitter.emit('openPublishedPanel', params)
}

export const PublishedContent = ({ defaultPanel = 'DEFAULT', isPreview, header, footer, showShareContent, onClose }: Props) => {
    const [panel, setPanel] = useState<PublishedPanelType>(defaultPanel)
    const { data } = useWxMiniProgramsAuthorizationResult()
    const isPublished = useAtomData(
        applicationVersionAtom,
        useCallback(s => s.status === APPLICATION_VERSION_STATUS.ONLINE, [])
    )
    const { auditInfo, releaseInfo } = data ?? {}
    const { status: auditStatus } = auditInfo ?? {}
    const { published } = releaseInfo ?? {}

    return useMemo(() => {
        switch (panel) {
            // case 'domain': {
            //     return <PublishDomain data={data} onOpenBind={handleOpenBind} onChangeMode={setMode} />
            // }
            case 'POSTER': {
                return <SharePoster isPreview={isPreview} onBack={() => setPanel('DEFAULT')} onClose={onClose} />
            }
            case 'QRCODE': {
                return <ShareQrCode isPreview={isPreview} onBack={() => setPanel('DEFAULT')} onClose={onClose} />
            }
            case 'SHARE': {
                return <SharedOtherPlatforms isPreview={isPreview} onBack={() => setPanel('DEFAULT')} onClose={onClose} />
            }
            case 'PRIVATE': {
                return <SharePrivateLink onBack={() => setPanel('DEFAULT')} onClose={onClose} />
            }
            case 'WX_PROGRAMS': {
                return <PublishWxPrograms onBack={() => setPanel('DEFAULT')} onClose={onClose} />
            }
            default: {
                return (
                    <>
                        {header}

                        {header ? <Divider mt={16} /> : null}

                        {showShareContent && (
                            <List>
                                <ListItem onClick={() => setPanel('SHARE')}>
                                    <ListItemIcon style={{ backgroundColor: '#54C5EB' }}>
                                        <IconFont type="ChatRoundLikeIconLibrary" size={20} />
                                    </ListItemIcon>
                                    <Text size={14}>{isPreview ? '通过社交媒体预览' : '分享到社交媒体'}</Text>
                                    <IconFont style={{ marginLeft: 'auto' }} type="ArrowRightSmall" color="var(--color-gray-400)" />
                                </ListItem>
                                <ListItem onClick={() => setPanel('POSTER')}>
                                    <ListItemIcon style={{ backgroundColor: 'var(--color-main)' }}>
                                        <IconFont type="Image" size={20} />
                                    </ListItemIcon>
                                    <Text size={14}>{isPreview ? '分享预览海报' : '分享海报'}</Text>
                                    <IconFont style={{ marginLeft: 'auto' }} type="ArrowRightSmall" color="var(--color-gray-400)" />
                                </ListItem>
                                <ListItem onClick={() => setPanel('QRCODE')}>
                                    <ListItemIcon style={{ backgroundColor: '#54C5EB' }}>
                                        <IconFont type="QRCodeIconLibrary" size={20} />
                                    </ListItemIcon>
                                    <Text size={14}>{isPreview ? '分享预览二维码' : '分享成二维码'}</Text>
                                    <IconFont style={{ marginLeft: 'auto' }} type="ArrowRightSmall" color="var(--color-gray-400)" />
                                </ListItem>
                                {!isPreview && (
                                    <ListItem onClick={() => setPanel('PRIVATE')}>
                                        <ListItemIcon style={{ background: 'linear-gradient(180deg, #667085 0%, #8B98B5 100%)' }}>
                                            <IconFont type="HandShake" size={20} fill="var(--color-white)" />
                                        </ListItemIcon>
                                        <Text size={14}>分享为私人模板</Text>
                                        <IconFont style={{ marginLeft: 'auto' }} type="ArrowRightSmall" color="var(--color-gray-400)" />
                                    </ListItem>
                                )}
                                {isPublished && (
                                    <ListItem onClick={() => setPanel('WX_PROGRAMS')}>
                                        <ListItemIcon
                                            style={{
                                                background: 'linear-gradient(180deg, rgba(18, 183, 106, 1) 0%, rgba(56, 209, 138, 1) 100%)'
                                            }}
                                        >
                                            <IconFont type="MiniProgram" size={20} fill="var(--color-white)" />
                                        </ListItemIcon>
                                        <Text size={14}>发布为微信小程序</Text>
                                        {published && (
                                            <Tooltip title="已发布正式版" placement="top">
                                                <SCxGreenRound />
                                            </Tooltip>
                                        )}
                                        <Flex style={{ flex: 1 }} alignItems="center" justifyContent="flex-end">
                                            <Text color="var(--color-gray-400)" size={14}>
                                                {auditStatus === WxProgramsAuditStatus.AUDITING ? '有正在审核的版本' : ''}
                                            </Text>
                                            <IconFont type="ArrowRightSmall" color="var(--color-gray-400)" />
                                        </Flex>
                                    </ListItem>
                                )}
                            </List>
                        )}
                        {footer}

                        {/* <Main style={style}> */}
                        {/* {headerDom}
                        {shareContentDom}
                        {actionsDom} */}
                        {/* </Main> */}
                    </>
                )
            }
        }
    }, [panel, isPreview, onClose, header, showShareContent, isPublished, published, auditStatus, footer])
}
